import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  TextField,
  CircularProgress,
  Typography,
  Box,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DescriptionIcon from '@material-ui/icons/Description';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import axios from '../../../utils/axios';
import './ExtractorFieldWidget.css';

// Store global para compartir datos de extracción entre widgets del mismo extractor
const extractionStore = {};
const extractorWidgets = {};
const extractorCallbacks = {};

const ExtractorFieldWidget = (props) => {
  const { id, value, onChange, schema, readonly, disabled, label, uiSchema } = props;
  const extractorId = schema.extractorId;
  const extractorFieldId = schema.extractorFieldId;
  const fieldLabel = label || schema.title || 'Campo';
  const placeholder = uiSchema?.['ui:placeholder'] || props.options?.placeholder || '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldValue, setFieldValue] = useState(value || '');
  const [isExtracted, setIsExtracted] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [progress, setProgress] = useState(0);
  const isFirstWidget = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!extractorId) return;

    if (!extractorWidgets[extractorId]) {
      extractorWidgets[extractorId] = [];
    }
    if (!extractorWidgets[extractorId].includes(id)) {
      extractorWidgets[extractorId].push(id);
    }
    isFirstWidget.current = extractorWidgets[extractorId][0] === id;
    setShowUploader(isFirstWidget.current && !extractionStore[extractorId]);

    if (!extractorCallbacks[extractorId]) {
      extractorCallbacks[extractorId] = {};
    }
    extractorCallbacks[extractorId][id] = (data) => {
      if (data[extractorFieldId] !== undefined) {
        const newValue = data[extractorFieldId] || '';
        setFieldValue(newValue);
        setIsExtracted(true);
        onChange(newValue);
      }
    };

    if (extractionStore[extractorId] && extractionStore[extractorId][extractorFieldId]) {
      const storedValue = extractionStore[extractorId][extractorFieldId];
      setFieldValue(storedValue);
      setIsExtracted(true);
      onChange(storedValue);
    }

    return () => {
      if (extractorCallbacks[extractorId]) {
        delete extractorCallbacks[extractorId][id];
      }
    };
  }, [extractorId, extractorFieldId, id, onChange]);

  const processFile = useCallback(async (file) => {
    if (!file) return;
    setFileName(file.name);
    setLoading(true);
    setError(null);
    setProgress(10);

    // Fake progress updates
    const progressInterval = setInterval(() => {
      setProgress((p) => (p < 85 ? p + 5 : p));
    }, 300);

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('extractorId', extractorId);
      const procedureId = window.location.pathname.split('/')[2];
      if (procedureId) {
        formData.append('procedureId', procedureId);
      }

      const response = await axios.post('/api/extraction/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (response.data && response.data.success && response.data.extractedData) {
        const fields = response.data.extractedData.fields || {};
        extractionStore[extractorId] = fields;
        setTimeout(() => setShowUploader(false), 400);

        if (extractorCallbacks[extractorId]) {
          Object.values(extractorCallbacks[extractorId]).forEach((callback) => {
            callback(fields);
          });
        }
      } else {
        setError(response.data?.extractedData?.errorMessage || 'No se recibieron datos extraídos');
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Error en extracción:', err);
      setError(err.response?.data?.error || 'Error al extraer datos del documento');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  }, [extractorId]);

  const handleFileUpload = useCallback(
    (event) => {
      processFile(event.target.files[0]);
    },
    [processFile]
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleManualChange = (event) => {
    const newValue = event.target.value;
    setFieldValue(newValue);
    onChange(newValue);
  };

  return (
    <Box className="gd-extractor-field" style={{ marginBottom: '20px' }}>
      <label className="gd-extractor-label" htmlFor={id}>
        {fieldLabel}
        {isExtracted && (
          <span className="gd-extracted-badge">
            <FlashOnIcon style={{ fontSize: 12 }} /> IA
          </span>
        )}
      </label>

      {showUploader && (
        <div
          className={`gd-dropzone ${dragActive ? 'drag-active' : ''} ${loading ? 'loading' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !loading && inputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <input
            ref={inputRef}
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            id={`extractor-upload-${extractorId}`}
            type="file"
            onChange={handleFileUpload}
            disabled={loading}
          />

          {!loading ? (
            <>
              <div className="gd-dropzone-icon">
                <CloudUploadIcon style={{ fontSize: 36 }} />
              </div>
              <div className="gd-dropzone-title">
                {dragActive ? 'Suelta el documento aquí' : 'Sube el documento'}
              </div>
              <div className="gd-dropzone-subtitle">
                Arrastra o haz clic — extraeremos todos los campos automáticamente con IA
              </div>
              <div className="gd-dropzone-formats">
                <span className="gd-format-chip">PDF</span>
                <span className="gd-format-chip">PNG</span>
                <span className="gd-format-chip">JPG</span>
              </div>
            </>
          ) : (
            <div className="gd-dropzone-loading">
              <div className="gd-loading-icon">
                <DescriptionIcon style={{ fontSize: 28 }} />
              </div>
              <div className="gd-loading-title">Analizando con IA...</div>
              <div className="gd-loading-filename">{fileName}</div>
              <div className="gd-progress-bar">
                <div className="gd-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="gd-loading-subtitle">
                GPT-4o está leyendo el documento y extrayendo los datos
              </div>
            </div>
          )}

          {error && <div className="gd-dropzone-error">⚠ {error}</div>}
        </div>
      )}

      {isExtracted && (
        <div className="gd-extracted-indicator">
          <CheckCircleIcon style={{ fontSize: 14 }} />
          <span>Extraído automáticamente</span>
        </div>
      )}

      <TextField
        id={id}
        value={fieldValue}
        onChange={handleManualChange}
        fullWidth
        variant="outlined"
        size="small"
        placeholder={placeholder}
        disabled={readonly || disabled}
        className={`gd-extractor-input ${isExtracted ? 'extracted' : ''}`}
        InputProps={{
          style: {
            backgroundColor: isExtracted ? '#ecfdf5' : '#ffffff',
            fontFamily: 'Inter, sans-serif',
            fontSize: 15,
            fontWeight: 500,
            borderRadius: 14,
            height: 48,
          },
        }}
      />
    </Box>
  );
};

export default ExtractorFieldWidget;
